import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, useWatch } from 'react-hook-form'
import { debounce } from 'lodash'
import { BoreholeFieldDefinitionsProps } from './BoreholeFieldDefinitions'
import { Section } from '../../common/Section'
import {
  BoreholeFieldUsageTable,
  BoreholeFieldUsageTableProps,
} from './BoreholeFieldUsageTable'
import { SingleSettingSection } from '../../common/SingleSettingSection'
import { ControlledRadioGroup } from '../../form/ControlledRadioGroup'
import { BoreholeConfirmationType } from '../../../generated/graphql'
import { BoreholeFieldConfirmation } from './BoreholeFieldConfirmation'
import { BoreholeFieldWarning } from './BoreholeFieldWarning'
import { FormGroup } from '../../form/FormGroup'
import { ControlledTextField } from '../../form/ControlledTextField'
import { useNotifications } from '../../../notifications/use-notifications'

type Props = BoreholeFieldDefinitionsProps & BoreholeFieldUsageTableProps

type FormValues = {
  description: string
  modelingInformation: string
  pathToSimulation: string
  confirmationType: BoreholeConfirmationType
}

export function BoreholeField({
  container,
  updateContainer,
  harvested,
  wasteInput,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const bottomRef = useRef<HTMLDivElement>(null)
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      description: container.description,
      modelingInformation: container.modelingInformation,
      pathToSimulation: container.pathToSimulation,
      confirmationType: container.confirmationType,
    },
  })
  const watchedConfirmationType = useWatch<BoreholeConfirmationType>({
    control,
    name: 'confirmationType',
  })
  const { enqueueSuccess } = useNotifications()

  const onWarningClick = () => {
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
    })
  }

  const onSubmit = async (values: FormValues) => {
    const {
      description,
      modelingInformation,
      pathToSimulation,
      confirmationType,
    } = values
    await updateContainer({
      description,
      modelingInformation,
      pathToSimulation,
      confirmationType,
    })

    enqueueSuccess(t('common.changesSaved'))
  }

  return (
    <>
      <BoreholeFieldWarning
        container={container}
        harvested={harvested}
        wasteInput={wasteInput}
        onWarningClick={onWarningClick}
      />

      <SingleSettingSection
        title={t('boreholeField.measurementBasisTitle')}
        subtitle={t('boreholeField.measurementBasisSubtitle')}
      >
        <ControlledRadioGroup
          control={control}
          name="confirmationType"
          options={Object.values(BoreholeConfirmationType).map(
            (boreholeConfirmationType) => ({
              value: boreholeConfirmationType,
              label: t(`boreholeConfirmationType.${boreholeConfirmationType}`),
            }),
          )}
          onChange={handleSubmit(onSubmit)}
          row={false}
        />
      </SingleSettingSection>

      <Section>
        <BoreholeFieldUsageTable
          harvested={harvested}
          wasteInput={wasteInput}
        />
      </Section>

      {watchedConfirmationType &&
        watchedConfirmationType !== BoreholeConfirmationType.NoConfirmation && (
          <>
            <SingleSettingSection title={t('boreholeField.description')}>
              <FormGroup>
                <ControlledTextField
                  control={control}
                  name="description"
                  label={t('boreholeField.description')}
                  minRows={5}
                  multiline
                  onChange={debounce(handleSubmit(onSubmit), 1000)}
                />
              </FormGroup>
            </SingleSettingSection>

            {watchedConfirmationType ===
              BoreholeConfirmationType.BasedOnModeling && (
              <>
                <SingleSettingSection
                  title={t('boreholeField.modelingInformation')}
                >
                  <FormGroup>
                    <ControlledTextField
                      control={control}
                      name="modelingInformation"
                      label={t('boreholeField.modelingInformation')}
                      minRows={10}
                      maxRows={10}
                      multiline
                      onChange={debounce(handleSubmit(onSubmit), 1000)}
                    />
                  </FormGroup>
                </SingleSettingSection>

                <SingleSettingSection
                  title={t('boreholeField.pathToSimulation')}
                >
                  <FormGroup>
                    <ControlledTextField
                      control={control}
                      name="pathToSimulation"
                      label={t('boreholeField.pathToSimulation')}
                      placeholder={t(
                        'boreholeField.pathToSimulationPlaceholder',
                      )}
                      onChange={debounce(handleSubmit(onSubmit), 1000)}
                    />
                  </FormGroup>
                </SingleSettingSection>
              </>
            )}

            <Section>
              <BoreholeFieldConfirmation
                container={container}
                updateContainer={updateContainer}
                harvested={harvested}
                wasteInput={wasteInput}
              />
              <div ref={bottomRef} />
            </Section>
          </>
        )}
    </>
  )
}
